// Canonical identity for a recurring series.
//
// A series is identified by description + type only — NOT amount. Real
// recurring charges (utilities, phone, usage-based bills, price-changed
// subscriptions) vary in amount month to month, so amount can't be part
// of the identity or those series fragment into singletons.
//
// The amount is a per-instance value; for projection/display we aggregate
// across a series' instances (see seriesAmount()).
//
// The description side is matched on its STABLE part. Banks stamp per-charge
// reference material into the descriptor — an invoice or statement number, the
// posting date, an ACH trace id — so the same bill arrives as
// "CITY WATER 0623", "CITY WATER 0723", "CITY WATER 0823". Matching the raw
// descriptor made each of those its own series, and since detection needs three
// instances under one key before it proposes anything, whole categories of
// bills (utilities, insurance, rent, payroll) could never be detected and never
// showed up in the recurring list at all.
//
// So seriesDescKey() drops volatile reference tokens and keeps everything else.
// It is deliberately NOT the display name (formatMerchantName): that collapses
// to the brand, which would fold every Zelle payment — or every Amazon order —
// into one "series". Distinguishing words survive here; only the parts that
// change from charge to charge are dropped.

export function normalizeDesc(desc) {
  return String(desc || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// "07/15", "6-23", "2026-06-15" — a posting date stamped into the descriptor.
const DATE_FRAGMENT = /\b\d{1,4}[/-]\d{1,2}(?:[/-]\d{1,4})?\b/g;
// "*" and "#" attach a reference to the name with no space ("AMAZON.COM*2X9AB",
// "STORE #4471"), so they separate tokens rather than belonging to one.
const REF_SEPARATOR = /[*#]+/g;

/**
 * Is this token per-charge reference material rather than part of the name?
 * Pure digits, or a token that is substantially digits ("2X9AB1CD3", "ID:8812").
 * A single digit is kept so brands like "7-Eleven" and "3M" survive.
 */
export function isReferenceToken(token) {
  const t = String(token).replace(/[^a-z0-9]/gi, '');
  if (!t) return true;
  if (/^\d+$/.test(t)) return true;
  const digits = (t.match(/\d/g) || []).length;
  return digits >= 2 && digits / t.length >= 0.34;
}

/** The stable part of a bank descriptor — see the module header. */
export function seriesDescKey(desc) {
  const normalized = normalizeDesc(desc);
  if (!normalized) return '';
  const kept = normalized
    .replace(DATE_FRAGMENT, ' ')
    .replace(REF_SEPARATOR, ' ')
    .split(' ')
    .filter((token) => token && !isReferenceToken(token))
    .join(' ')
    .trim();
  // A descriptor that is nothing BUT a reference ("POS 4471882") still needs a
  // key; falling back to the raw form keeps those rows distinct from each other
  // instead of collapsing them all into one empty-keyed series.
  return kept || normalized;
}

// A transaction can carry an explicit `seriesKey`: set when the user links a
// charge whose descriptor changed to the series it belongs to (missed.js). It
// wins over the descriptor — that is what keeps the charge in its series.
export function seriesKeyOf(tx) {
  if (tx.seriesKey) return tx.seriesKey;
  return `${seriesDescKey(tx.description)}__${tx.type}`;
}

// The identity as it was keyed before seriesDescKey() — the raw normalized
// description. Series keys are persisted (custom names, dismissed suggestions,
// what-if scenario deltas), so anything that reads a STORED key has to accept
// the legacy form too or the user's saved state silently detaches from its
// series. Nothing should ever WRITE this form.
export function legacySeriesKeyOf(tx) {
  // An explicit key was never stored under any older form.
  if (tx.seriesKey) return tx.seriesKey;
  return `${normalizeDesc(tx.description)}__${tx.type}`;
}

/** Every key a stored reference to this transaction's series could be under. */
export function seriesKeyAliases(tx) {
  const key = seriesKeyOf(tx);
  const legacy = legacySeriesKeyOf(tx);
  return legacy === key ? [key] : [key, legacy];
}

// Representative per-occurrence amount for a set of instances of one series.
// Uses the mean — strictly more accurate than "last amount" for variable
// bills, and identical to the fixed amount when amounts don't vary.
export function seriesAmount(instances) {
  if (!instances || instances.length === 0) return 0;
  const sum = instances.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  return sum / instances.length;
}
